/* First-party conversion signals. Never read engraving content, artwork, contacts or error text. */
(() => {
  'use strict';
  if (globalThis.JaxStorefrontAnalytics) return;
  const actions = new Set(['form_started', 'mode_change', 'font_change', 'flower_change', 'layout_change', 'upload_selected', 'validation_error', 'cart_attempt', 'cart_success', 'cart_error', 'edit_opened', 'edit_saved', 'edit_error']);
  const errors = new Set(['validation_failed', 'cart_failed', 'cart_unconfirmed', 'variant_unavailable', 'design_load_failed', 'design_save_failed']);
  const started = new WeakSet(), recent = new WeakMap();
  const numericId = value => /^\d{1,24}$/.test(String(value || '')) ? String(value) : undefined;
  const qa = () => new URLSearchParams(location.search).get('je_analytics_test') === '1';
  function allowed() {
    try {
      if (!qa() && (Shopify.designMode || (Shopify.theme?.role && Shopify.theme.role !== 'main'))) return false;
      return Shopify.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch (_) { return false; }
  }
  function send(name, data) {
    try {
      if (!allowed() || typeof globalThis.Shopify?.analytics?.publish !== 'function') return false;
      const result = globalThis.Shopify?.analytics?.publish?.(name, { ...data, ...(qa() ? { isTest: true } : {}) });
      result?.catch?.(() => {});
      return true;
    } catch (_) { return false; }
  }
  const quoteActions = new Set(['viewed', 'started', 'submitted', 'validation_error']);
  const quoteSeen = new Map();
  const quoteUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  function quoteCategories(form) {
    const value = name => String(form.querySelector('[name="contact[' + name + ']"]')?.value || '').trim().toLowerCase();
    const quantity = value('Quantity').replace(/[–—]/g, '-');
    const bands = { 'under 10': 'under_10', '10-23': '10_23', '24-47': '24_47', '48-99': '48_99', '100-999': '100_999', '1000+': '1000_plus', '1,000+': '1000_plus' };
    const product = value('Product'), style = value('Same on every piece');
    return {
      quantity_band: bands[quantity] || 'unknown',
      product_family: /^stanley/.test(product) ? 'stanley' : /^owala/.test(product) ? 'owala' : /^mini/.test(product) ? 'mini' : /^can coolers?/.test(product) ? 'can_cooler' : /^yeti/.test(product) ? 'yeti' : /^a mix of products$/.test(product) ? 'mixed' : 'unknown',
      personalization_type: /^same logo on every piece$/.test(style) ? 'same_logo' : /^one logo and a different name on each$/.test(style) ? 'individual_names' : /^a mix of both$/.test(style) ? 'mixed' : 'unknown',
    };
  }
  function trackQuote(form, details = {}) {
    try {
      if (!allowed() || form?.id !== 'CorporateQuote' || !form.matches?.('form.corporate__form') || !quoteActions.has(details.action) || typeof details.flow_id !== 'string' || !quoteUuid.test(details.flow_id)) return false;
      if (details.action === 'submitted' && !form.querySelector('[data-quote-success]')) return false;
      const flow_id = details.flow_id.toLowerCase(), key = flow_id + ':' + details.action, previous = quoteSeen.get(key);
      if (previous !== undefined && (details.action !== 'validation_error' || Date.now() - previous < 500)) return false;
      const extra = { flow_id, ...(details.action === 'submitted' ? {} : quoteCategories(form)) };
      const sent = send('bulk_quote_action', { action: details.action, extra });
      if (sent) { quoteSeen.set(key, Date.now()); if (quoteSeen.size > 500) quoteSeen.delete(quoteSeen.keys().next().value); }
      return sent;
    } catch (_) { return false; }
  }
  document.addEventListener('bulk-quote:action', event => trackQuote(event.target, event.detail));
  function trackForm(subject, action, details = {}) {
    try {
      const bloom = subject?.id === 'je-birthflower-product' ? subject : null;
      const form = bloom || (subject?.matches?.('form') ? subject : subject?.closest?.('form') || subject?.querySelector?.('form'));
      if ((!bloom && !form?.querySelector('[data-je-root]')) || !actions.has(action) || !allowed()) return false;
      const previous = recent.get(form);
      if (previous?.action === action && Date.now() - previous.at < 500) return false;
      const extra = { studio: false };
      if (errors.has(details.error_code)) extra.error_code = details.error_code;
      if (['text', 'logo'].includes(details.tool)) extra.tool = details.tool;
      if (['png', 'jpeg', 'webp', 'svg', 'pdf', 'other'].includes(details.file_type)) extra.file_type = details.file_type;
      if (['small', 'medium', 'large'].includes(details.file_size_bucket)) extra.file_size_bucket = details.file_size_bucket;
      const sent = send('personalization_action', {
        productId: numericId(bloom?.dataset.productId || form.closest('product-info')?.dataset.productId),
        variantId: numericId(bloom?.dataset.variantId || form.querySelector('[name="id"]')?.value), action, extra,
      });
      if (sent) recent.set(form, { action, at: Date.now() });
      return sent;
    } catch (_) { return false; }
  }
  function start(field) {
    const form = field?.closest?.('form');
    if (form && !started.has(form) && trackForm(form, 'form_started')) started.add(form);
  }
  document.addEventListener('input', event => {
    if (event.isTrusted && event.target?.closest?.('[data-je-root]')) start(event.target);
  }, { passive: true });
  document.addEventListener('change', event => {
    try {
      const field = event.target;
      if (!field?.closest?.('[data-je-root]')) return;
      // Restoring a cart line dispatches synthetic changes; don't call those shopper choices.
      if (!event.isTrusted) return;
      start(field);
      if (field.matches('[data-je-type]')) trackForm(field, 'mode_change', { tool: field.value === 'Logo' ? 'logo' : 'text' });
      else if (field.matches('[data-je-font]')) trackForm(field, 'font_change');
      else if (field.matches('[data-je-template-dd]')) trackForm(field, 'flower_change');
      else if (field.name === 'properties[Text position]') trackForm(field, 'layout_change');
      else if (field.type === 'file' && field.files?.length) {
        const file = field.files[0];
        const types = { 'image/png': 'png', 'image/jpeg': 'jpeg', 'image/webp': 'webp', 'image/svg+xml': 'svg', 'application/pdf': 'pdf' };
        trackForm(field, 'upload_selected', { file_type: types[file.type] || 'other', file_size_bucket: file.size < 1024 * 1024 ? 'small' : file.size < 5 * 1024 * 1024 ? 'medium' : 'large' });
      }
    } catch (_) { /* Tracking must never interrupt personalization. */ }
  }, { passive: true });
  document.addEventListener('click', event => {
    try {
      if (!event.isTrusted) return;
      const choice = event.target?.closest?.('[data-je-font-option], [data-je-flower-option]');
      if (choice) {
        start(choice);
        trackForm(choice, choice.matches('[data-je-font-option]') ? 'font_change' : 'flower_change');
        return;
      }
      const link = event.target?.closest?.('a[href]');
      if (!link || link.closest('engraving-studio-entry, studio-showcase, [data-je-buybar-link]')) return;
      const target = new URL(link.href, location.href);
      if (target.origin !== location.origin || !(target.pathname === '/pages/engraving-studio' || target.searchParams.get('view') === 'studio-customize')) return;
      const entry = link.closest('header, header-drawer') ? 'header' : link.closest('product-info') ? 'product' : location.pathname === '/' && link.closest('main') ? 'home_hero' : 'other';
      send('studio_cta_click', { action: 'click', extra: { entry_source: entry } });
    } catch (_) { /* Ignore malformed links, never export them. */ }
  }, { passive: true });
  globalThis.JaxStorefrontAnalytics = Object.freeze({ trackForm, trackQuote });
})();
